import { useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

// ANT DESIGN IMPORTS
import { ReloadOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Input,
  Row,
  Select,
  Spin,
  Table,
  Tag,
  Typography,
} from "antd";

// PROJECT IMPORTS
import MainLayout from "../../../components/layouts/MainLayout";
import TopContent from "../../../components/layouts/TopContent";
import appLabel from "../../../config/appLabel";
import appStatic from "../../../config/appStatic";
import { rightAction } from "../../../redux/actions";
import definedApi from "../../../services/definedApi";
import cnt from "../../../services/definedConstant";
import { BOM } from "../../../services/definedMethod";
import { labelConfig } from "../product.config";
import CustomPagination from "../../../components/CustomPagination";

const rowSelection = {
  getCheckboxProps: (record) => ({
    disabled: record.name === "Disabled User",
    name: record.name,
  }),
};

const columns = [
  {
    title: "Name",
    dataIndex: "name",
    sorter: (a, b) => a.name.localeCompare(b.name),
  },
  {
    title: "Short Name",
    dataIndex: "shortName",
    sorter: (a, b) => a["shortName"].localeCompare(b["shortName"]),
  },
  {
    title: "Currency",
    dataIndex: "currency",
    sorter: (a, b) => a["currency"].localeCompare(b["currency"]),
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    render: (text) => {
      let bgColor = "#F9FAFB"; // default bg
      let textColor = "#344054"; // default text
      let border = "1px solid #EAECF0";

      switch (text?.toLowerCase()) {
        case "active":
          bgColor = "#ECFDF3";
          textColor = "#067647";
          border = "1px solid #ABEFC6";
          break;
        case "inactive":
          bgColor = "#FEE4E2";
          textColor = "#B42318";
          border = "1px solid #F0ADAD";
          break;

        default:
          break;
      }

      return (
        <Tag
          style={{
            padding: "2px 12px",
            borderRadius: "16px",
            border: border,
            fontSize: "12px",
            fontWeight: 500,
            display: "inline-block",
            backgroundColor: bgColor,
            color: textColor,
            textTransform: "capitalize",
          }}
        >
          {text}
        </Tag>
      );
    },
  },
];

const productStatusOptions = [
  { value: "status", label: "All Status" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const PRD0400 = (props) => {
  const { userData, rightBtn, rightAction } = props;
  const { session } = userData;
  const { Text } = Typography;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRow, setSelectedRow] = useState(null);
  const [tableCount, setTableCount] = useState("10");
  const [redeList, setRedeList] = useState([]);
  const [originalList, setOriginalList] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("active");
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isSearch, setIsSearch] = useState(false);

  //DECLARED METHODS
  function handleSearchInTable(searchKey) {
    setIsSearch(true);
    searchKey = searchKey.target.value;

    if (!searchKey) {
      setRedeList(originalList);
      return;
    }
    const filterBySearch = originalList.filter(
      (item) =>
        item?.name.toLowerCase()?.includes(searchKey.toLowerCase()) ||
        item?.shortName.toLowerCase()?.includes(searchKey.toLowerCase()) ||
        item?.currency.toLowerCase()?.includes(searchKey.toLowerCase()),
    );
    setRedeList(filterBySearch);
  }

  function selectTableCount(value) {
    setTableCount(value);
  }

  const onRefresh = (offset = 0) => {
    getRedeListAction(selectedStatus, offset, Number(tableCount));
  };
  //END METHODS DECLARED

  useEffect(() => {
    BOM.HasPermission("READ_RECURRINGDEPOSITPRODUCT") &&
      getRedeListAction(selectedStatus);
    BOM.HasPermission("CREATE_RECURRINGDEPOSITPRODUCT") &&
      rightAction(cnt.RIGHT_ADD_BTN);
  }, [selectedStatus]);

  const getRedeListAction = (
    status = selectedStatus,
    offset = 0,
    limit = Number(tableCount),
  ) => {
    const params = {
      ...(status !== "status" && { status }),
      offset,
      limit,
    };
    let body = {
      session,
      params,
    };

    setCurrentPage(1);
    setIsLoading(true);
    setIsSearch(false);
    BOM.FetchReqAction(
      body,
      definedApi.GetRecurringDepositProductList,
      (err, res) => {
        if (err) {
          BOM.AlertMsg(cnt.DANGER, err.defaultUserMessage);
          setIsLoading(false);
        } else {
          const objList = res?.pageItems;
          let tempArray = [];

          objList.forEach((element) => {
            tempArray.push({
              key: element?.id,
              name: element?.name ? element?.name : "-",
              shortName: element?.shortName ? element?.shortName : "-",
              currency: element?.currency
                ? element?.currency?.displayLabel
                : "-",
              status: element?.status,
            });
          });

          //Display only active records list on the table; this will change after switching from authorized to disabled and vice versa.
          // setRedeList(tempArray.filter((item) => item.disabled === false));
          setRedeList(tempArray);
          setTotalItems(res?.totalFilteredRecords);

          //keep the orginal data to use after gList is modified when change from autorise to disable
          setOriginalList(tempArray);

          //STOP LOAD
          setIsLoading(false);
        }
      },
    );
  };

  const getCsvListData = () => {
    return new Promise((resolve, reject) => {
      const params = {
        limit: 1000,
        offset: 0,
      };

      let body = {
        session,
        params,
      };

      BOM.FetchReqAction(
        body,
        definedApi.GetRecurringDepositProductList,
        (err, res) => {
          if (err) {
            reject(err);
          } else {
            const objList = res;
            let tempArray = [];

            objList.forEach((element) => {
              tempArray.push({
                key: element?.id,
                name: element?.name ? element?.name : "-",
                shortName: element?.shortName ? element?.shortName : "-",
                currency: element?.currency
                  ? element?.currency?.displayLabel
                  : "-",
                status: element?.status,
              });
            });

            resolve(tempArray);
          }
        },
      );
    });
  };

  // FLOAT METHODS
  const onSelectItemAction = (selectedRowKeys, objParam) => {
    //HOLD THE OBJECT SELECTED HERE
    setSelectedRow(objParam[0]);
    [
      BOM.HasPermission("READ_RECURRINGDEPOSITPRODUCT") && cnt.RIGHT_DETAIL_BTN,
      BOM.HasPermission("UPDATE_RECURRINGDEPOSITPRODUCT") && cnt.RIGHT_EDIT_BTN,
    ].map((item) => {
      dispatch({
        type: item,
      });
    });
  };

  function handleOnView() {
    delete selectedRow.amount;
    selectedRow.amount = selectedRow.amountToUse;
    const { detailBtn } = rightBtn;
    if (detailBtn.toLowerCase() === "primary") {
      navigate("/view-prd0400", { state: selectedRow });
    } else {
      console.log("Please choose a record from the table to open");
    }
  }
  function handleOnCreate() {
    const { addBtn } = rightBtn;
    if (addBtn.toLowerCase() === "primary") {
      navigate("/add-prd0400", { ofObj: selectedRow });
    } else {
      console.log("Could not open the link");
    }
  }
  function handleOnEdit() {
    const { editBtn } = rightBtn;
    if (editBtn.toLowerCase() === "primary") {
      navigate("/edit-prd0400", {
        state: { id: selectedRow.key, ...selectedRow.otherKeys },
      });
    } else {
      console.log("Please choose a record from the table to edit");
    }
  }

  return (
    <MainLayout
      isEnableRightSidebar={true}
      onView={handleOnView}
      onAdd={handleOnCreate}
      onEdit={handleOnEdit}
    >
      <TopContent
        pageTitle={appLabel.RecurringDepositProducts}
        pageItems={appStatic.recurringDepositProductItems}
      />

      <Card
        title={
          <>
            {`${appLabel.RecurringDepositProduct} List`}
            <Button
              type="primary"
              shape="round"
              size="small"
              className="of-primaryBtnColor"
              onClick={() => onRefresh()}
              icon={<ReloadOutlined />}
              loading={isLoading}
            >
              {labelConfig.refresh}
            </Button>
          </>
        }
        style={{
          boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
          borderRadius: 20,
        }}
      >
        {/*RENDER TABLE LIST HERE*/}

        <Spin spinning={isLoading} size="large">
          <Row style={{ marginBottom: 20 }}>
            <div style={{ marginBottom: "auto" }}>
              <Text style={{ marginRight: 8 }}>Show</Text>
              <Select
                defaultValue={tableCount}
                options={[
                  { value: "5", label: "5" },
                  { value: "10", label: "10" },
                  { value: "25", label: "25" },
                  { value: "50", label: "50" },
                ]}
                onSelect={selectTableCount}
              />
              <Text style={{ marginLeft: 8 }}>Entries</Text>
            </div>

            <div style={{ margin: "0px 10px" }}>
              <Input
                onChange={handleSearchInTable}
                placeholder="Search by Name, Short Name and Currency"
                style={{
                  width: 400,
                }}
              />
            </div>
            <div style={{ marginBottom: "auto", marginLeft: 10 }}>
              <Select
                value={selectedStatus}
                options={productStatusOptions}
                onChange={setSelectedStatus}
                placeholder={"Select Status"}
                dropdownStyle={{ width: "250px" }}
                className="interbank-table-select"
              />
            </div>
            {/* <DownloadTableButtons
              downloadData={redeList}
              columns={columns}
              fileName={"Recurring Deposit Product List"}
              onExportClick={getCsvListData}
            /> */}
          </Row>
          <Table
            rowSelection={{
              type: "radio",
              ...rowSelection,
              onChange: onSelectItemAction,
            }}
            columns={columns}
            dataSource={redeList}
            bordered
            pagination={false}
          />
          {!isSearch && (
            <CustomPagination
              totalItems={totalItems}
              pageSize={Number(tableCount)}
              onPageChange={(page, offset) => onRefresh(offset)}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
            />
          )}
        </Spin>
      </Card>
    </MainLayout>
  );
};
const mapStateToProps = (state) => ({
  userData: state.AuthenticationData.userData,
  rightBtn: state.RightSideBtn,
});

const mapDispatchToProps = { rightAction };
export default connect(mapStateToProps, mapDispatchToProps)(PRD0400);
